import EditIcon from "@mui/icons-material/Edit";
import InfoIcon from "@mui/icons-material/Info";
import {
  alpha,
  Box,
  Button,
  Checkbox,
  Chip,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  Paper,
  TextField,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import { useState } from "react";
import { CategoryRounded } from "@mui/icons-material";
import PageHeader from "../components/page-header";
import { Dialogs } from "../components/dialogs";
import StickyHeadTable, { Column } from "../components/table";
import { Tema } from "../types/general";
import { ViewModelTema } from "../viewmodel/viewmodel-tema";

function TemasPage() {
  const theme = useTheme();
  const {
    user,
    dialog,
    handleDeleteTema,
    showWarning,
    handleActionTema,
    showConfirmation,
    ...viewModel
  } = ViewModelTema();

  const [open, setOpen] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [nome, setNome] = useState("");
  const [available, setAvailable] = useState(true);
  const [loading, setLoading] = useState(false);

  const handleOpen = () => {
    setEditMode(false);
    setNome("");
    setAvailable(true);
    setOpen(true);
  };

  const handleEdit = (id: number) => {
    const tema = viewModel.temas.data.find((t) => t.id === id);
    if (tema) {
      setEditMode(true);
      setNome(tema.nome);
      setAvailable(tema.available ?? true);
      viewModel.selectTema(tema);
      setOpen(true);
    }
  };



  const handleSubmit = async () => {
    if (!nome.trim()) {
      showWarning({
        title: "Erro",
        message: "O nome do tema é obrigatório"
      });
      return;
    }
    setOpen(false);
    editMode && viewModel.selectedTema?.id
      ? handleActionTema({
        id: viewModel.selectedTema.id, nome, available
      }, 'update')
      : handleActionTema({
        nome, available
      } as Tema, 'add');
  };

  const handleDelete = (id: number) => {
    const tema = viewModel.temas.data.find((t) => t.id === id);
    showConfirmation({
      title: "Confirmar Eliminação",
      message: `Tem a certeza que deseja eliminar o tema "${tema?.nome}"? Esta ação não pode ser desfeita.`,
      onConfirm: () => handleDeleteTema(id),
    });
  };

  const columns: Column<Tema>[] = [
    { id: "id", label: "ID", minWidth: 100 },
    { id: "nome", label: "Nome", minWidth: 400 },
    {
      id: "perguntas", label: "Perguntas", minWidth: 100, renderCell: (row) => (
        <p>
          {
            (Number(row.easy) ?? 0) + (Number(row.medium) ?? 0) + (Number(row.dificult) ?? 0)
          }
        </p>
      ),
    },
    {
      id: "available",
      label: "Disponível",
      minWidth: 130,
      renderCell: (row) => (
        <Chip
          label={row.available ? "Sim" : "Não"}
          color={row.available ? "success" : "default"}
          size="small"
        />
      ),
    },
    {
      id: "actions",
      label: "Ações",
      minWidth: 190,
      renderCell: (row) => (
        <Box>
          <Tooltip title="Editar" arrow>
            <IconButton
              size="small"
              onClick={() => handleEdit(row.id!)}
              color="primary"
            >
              <EditIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          {/* <Tooltip title="Eliminar" arrow>
            <IconButton
              size="small"
              color="error"
              onClick={() => handleDelete(row.id!)}
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Tooltip> */}
          <Tooltip title="Ver Perguntas" arrow>
            <Button
              size="small"
              color="success"
              onClick={() => viewModel.goToPerguntas(row.id!)}
            >
              Perguntas
            </Button>
          </Tooltip>
        </Box>
      ),
    },
  ];

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 2 }}>
      <PageHeader
        icon={<CategoryRounded />}
        color="#6157E0"
        title="Gestão de Temas"
        actions={
          user?.access === "ADMIN" && (
            <Button variant="contained" onClick={handleOpen}>
              Novo Tema
            </Button>
          )
        }
      />

      <Paper
        elevation={0}
        sx={{
          p: 2,
          mb: 3,
          background: alpha(theme.palette.info.main, 0.06),
          border: `1px solid ${alpha(theme.palette.info.main, 0.25)}`,
          borderRadius: 3,
          display: "flex",
          alignItems: "center",
          gap: 2,
        }}
      >
        <Box
          sx={{
            backgroundColor: theme.palette.info.main,
            borderRadius: "50%",
            width: 40,
            height: 40,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
          }}
        >
          <InfoIcon sx={{ color: "white", fontSize: 24 }} />
        </Box>
        <Box flex={1}>
          <Typography variant="subtitle2" fontWeight="bold" color="info.main">
            Requisitos Mínimos de Perguntas
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            Para cada tema é necessário adicionar no mínimo:
            <Box component="span" fontWeight="bold" color="text.primary">
              {" "}16 fáceis, 32 médias e 32 difíceis
            </Box>
          </Typography>
        </Box>
      </Paper>
      <StickyHeadTable
        columns={columns}
        rows={viewModel.temas.data}
        getRowId={(row) => row.id!}
        pagination={{
          totalData: viewModel.temas.pagination.totalData,
          totalPages: viewModel.temas.pagination.totalPages,
          currentPage: viewModel.temas.pagination.currentPage,
          onPageChange: (newPage) => {
            viewModel.fetchTemas(newPage);
          },
          onRowsPerPageChange: () => { },
        }}
      />

      {/* Create/Edit Dialog */}
      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editMode ? "Editar Tema" : "Novo Tema"}</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Nome do Tema"
            fullWidth
            value={nome}
            onChange={(e) => setNome(e.target.value)}
            disabled={loading}
            sx={{ mb: 2 }}
          />
          {
            editMode && (
              <FormControlLabel
                control={
                  <Checkbox
                    checked={available}
                    onChange={(e) => setAvailable(e.target.checked)}
                    disabled={loading}
                  />
                }
                label="Disponível para uso"
              />
            )
          }
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)} disabled={loading}>
            Cancelar
          </Button>
          <Button onClick={handleSubmit} variant="contained" disabled={loading}>
            {loading ? "A processar..." : editMode ? "Atualizar" : "Criar"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialogs{...dialog} />
    </Container>
  );
}

export default TemasPage;
